import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";

import useLanguageChange from "../../../../hooks/useLanguageChange";
import { MEDIA_PERMISSIONS } from "../../../../constants/CallSettingConstants";
import { LogFirebase } from "../../../../services/firestore";
import { setCurrentAudioDevice } from "../../../../reducers/call-setting-reducer";
import navigatorService from "../../../../services/navigatorService";

const MicrophoneDropDown = ({ askPermission, audioDeviceUpdate }) => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();

    const { isMicExist, audioPermission, audioDevices, currentAudioDevice } = useSelector((state) => state.callSetting);

    const handleAudioDeviceChange = (device) => {
        if (currentAudioDevice?.deviceId !== device.deviceId) {
            dispatch(setCurrentAudioDevice(device));
            LogFirebase("CallSettings", "selectedMic", device.label);
            audioDeviceUpdate();
        }
    };

    const audioErrorMsg = useMemo(() => {
        let text = "";
        if (audioPermission === MEDIA_PERMISSIONS.BLOCKED) {
            if (!navigatorService.isSafari()) {
                text = (
                    <>
                        {`${localisedValues["allow_access_to_mic"]} ${localisedValues["click_the_padlock"]} `}
                        <span className='for-inline-icon icon-lock'></span> {localisedValues["to_the_left_of_a_web_page_address"]}
                    </>
                );
            } else {
                text = localisedValues["microphone_permission_disabled"] + localisedValues["permission_error_message_microphone_browser"];
            }
        } else if (audioPermission === MEDIA_PERMISSIONS.ERROR) {
            text = localisedValues["microphone_permission_disabled"];
        }

        return text;
    }, [audioPermission, localisedValues]);

    const microphoneLabelText = useMemo(() => {
        let text = "";
        if (!isMicExist) {
            text = localisedValues["microphone_not_found"];
        } else if (audioPermission === MEDIA_PERMISSIONS.PROMPT) {
            text = localisedValues["grant_permission"];
        } else if (audioPermission === MEDIA_PERMISSIONS.ERROR || audioPermission === MEDIA_PERMISSIONS.BLOCKED) {
            text = localisedValues["microphone_blocked"];
        } else if (audioPermission === MEDIA_PERMISSIONS.GRANTED) {
            text = currentAudioDevice?.label;
        }

        return text;
    }, [isMicExist, audioPermission, currentAudioDevice, localisedValues]);

    const onClickPermissionDropdown = () => {
        if (
            isMicExist &&
            (audioPermission === MEDIA_PERMISSIONS.PROMPT ||
                audioPermission === MEDIA_PERMISSIONS.ERROR ||
                audioPermission === MEDIA_PERMISSIONS.BLOCKED)
        ) {
            askPermission();
        }
    };

    return (
        <div className='form-group'>
            <label className='form-label'>{localisedValues["microphone"]}</label>
            <div className='dropdown custom-dropdown dropdown-select call-settings-dropdown'>
                <button
                    className='btn dropdown-toggle triangle-none'
                    type='button'
                    data-bs-toggle={audioPermission === MEDIA_PERMISSIONS.GRANTED && "dropdown"}
                    aria-expanded='false'
                    disabled={audioPermission === MEDIA_PERMISSIONS.ERROR || audioPermission === MEDIA_PERMISSIONS.BLOCKED || !isMicExist}
                    onClick={onClickPermissionDropdown}
                >
                    <span className='btn-text'>{microphoneLabelText}</span>
                    <span className='dropdown-toggle-arrow icon-down-arrow-light'></span>
                </button>
                {audioPermission === MEDIA_PERMISSIONS.GRANTED && (
                    <div className='dropdown-menu'>
                        <div className='dropdown-menu-in custom-scroll-thin'>
                            <ul>
                                {audioDevices.map((audio) => {
                                    const isSelectedMicrophone = audio.deviceId === currentAudioDevice?.deviceId;
                                    return (
                                        <li key={audio.deviceId}>
                                            <button
                                                className={clsx("btn dropdown-item", isSelectedMicrophone && "active")}
                                                onClick={() => handleAudioDeviceChange(audio)}
                                            >
                                                {audio.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
            {(audioPermission === MEDIA_PERMISSIONS.BLOCKED || audioPermission === MEDIA_PERMISSIONS.ERROR) && (
                <div className='validation-error'>{audioErrorMsg}</div>
            )}
        </div>
    );
};

MicrophoneDropDown.propTypes = {
    askPermission: PropTypes.func,
    audioDeviceUpdate: PropTypes.func,
};

export default MicrophoneDropDown;
